import { constant } from "common/constant";
import moment from "moment";

/**
 * Format ISO date string to "DD-MMM-YYYY HH:MM am/pm"
 * @param {string|Object} dateInput - ISO string or object with date property
 * @returns {string} Formatted date string
 */
const formatDate = (dateInput) => {
  // Handle both direct string and object with date property
  const isoString = typeof dateInput === 'object' && dateInput?.date
    ? dateInput.date
    : dateInput;

  if (!isoString) return "-";

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return constant?.invalidDate || "Invalid Date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "long" }).substring(0, 3);
    const year = date.getFullYear();

    // Fix: Convert to 12-hour format properly
    let hours = date.getHours();
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;
    const hoursStr = String(hours).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return constant?.invalidDate || "Invalid Date";
  }
};

/**
 * Main DateFormatter function
 * @param {string|Object} dateString - Date string or object with date property
 * @returns {string} Formatted date
 */
const DateFormatter = (dateString) => {
  return formatDate(dateString);
};

/**
 * Format date to DD-MM-YYYY
 * @param {string} date - Date string
 * @returns {string} Formatted date
 */
export const formatDateTime = (date) => {
  if (!date) return "-";

  try {
    const momentDate = moment(date);
    if (!momentDate.isValid()) return constant?.invalidDate || "Invalid Date";
    return momentDate.format("DD-MM-YYYY");
  } catch (error) {
    console.error("Error formatting date:", error);
    return constant?.invalidDate || "Invalid Date";
  }
};

/**
 * Convert "DD-MMM-YYYY HH:MM am/pm" to ISO format "YYYY-MM-DDTHH:MM"
 * @param {string} timestamp - Timestamp string
 * @returns {string|null} ISO formatted string or null
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== "string") return null;

  try {
    if (timestamp.includes(" ")) {
      const dateParts = timestamp.split(" ");
      const [day, month, year] = dateParts[0].split("-");
      const timeParts = dateParts[1].split(":");

      if (!timeParts[0] || !timeParts[1]) return null;

      const hour = timeParts[0];
      const minutes = timeParts[1];

      const monthMap = {
        Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
        Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
      };

      const formattedMonth = monthMap[month];
      if (formattedMonth) {
        return `${year}-${formattedMonth}-${day}T${hour}:${minutes}`;
      }
    }
  } catch (error) {
    console.error("Error formatting timestamp:", error);
  }

  return null;
};

/**
 * Convert "DD-MMM-YYYY HH:MM am/pm" to datetime-local format "YYYY-MM-DDTHH:MM"
 * @param {string} dateString - Date string
 * @returns {string} Formatted date for datetime-local input
 */
export const formatDateString = (dateString) => {
  if (!dateString) return "";

  try {
    const regex = /^(\d{2})-(\w{3})-(\d{4}) (\d{2}):(\d{2}) (am|pm)$/i;
    const match = dateString.match(regex);

    if (!match) {
      console.error("Invalid date format:", dateString);
      return "";
    }

    const [, day, month, year, hours, minutes, period] = match;

    const monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };

    const monthNumber = monthMap[month];
    if (monthNumber === undefined) {
      console.error("Invalid month:", month);
      return "";
    }

    let hours24 = parseInt(hours, 10);

    // Convert to 24-hour format
    if (period.toLowerCase() === "pm" && hours24 < 12) {
      hours24 += 12;
    } else if (period.toLowerCase() === "am" && hours24 === 12) {
      hours24 = 0;
    }

    const date = new Date(year, monthNumber, day, hours24, parseInt(minutes, 10));

    if (isNaN(date.getTime())) {
      console.error("Invalid date:", dateString);
      return "";
    }

    // Format to YYYY-MM-DDTHH:MM for datetime-local input
    const formattedYear = date.getFullYear();
    const formattedMonth = String(date.getMonth() + 1).padStart(2, "0");
    const formattedDay = String(date.getDate()).padStart(2, "0");
    const formattedHours = String(date.getHours()).padStart(2, "0");
    const formattedMinutes = String(date.getMinutes()).padStart(2, "0");

    return `${formattedYear}-${formattedMonth}-${formattedDay}T${formattedHours}:${formattedMinutes}`;
  } catch (error) {
    console.error("Error in formatDateString:", error);
    return "";
  }
};

/**
 * Simple date formatter without time - DD MMM YYYY
 * @param {string} date - Date string
 * @returns {string} Formatted date
 */
export const formatDateOnly = (date) => {
  if (!date) return "-";

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return constant?.invalidDate || "Invalid Date";

    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return constant?.invalidDate || "Invalid Date";
  }
};

/**
 * Compact date formatter - D/M/YY
 * @param {string|Date} date - Date string or Date
 * @returns {string} Formatted date
 */
export const formatDateCompact = (date) => {
  if (!date) return "-";

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return constant?.invalidDate || "Invalid Date";

    const day = d.getDate();
    const month = d.getMonth() + 1;
    const yearShort = String(d.getFullYear()).slice(-2);

    return `${day}/${month}/${yearShort}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return constant?.invalidDate || "Invalid Date";
  }
};

/**
 * Readable date formatter - "MMM D, YYYY"
 * @param {string|Date} date - Date string or Date
 * @returns {string} Formatted date
 */
export const formatDateLong = (date) => {
  if (!date) return "-";

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return constant?.invalidDate || "Invalid Date";

    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return constant?.invalidDate || "Invalid Date";
  }
};

export const formatYearRange = (year) => {
  if (!year) return "-";
  try {
    const startYear = parseInt(year);
    if (isNaN(startYear)) return year.toString();
    const nextYearShort = (startYear + 1).toString().slice(-2);
    return `${startYear}-${nextYearShort}`;
  } catch (error) {
    return year.toString();
  }
};

export default DateFormatter;
